/**
 * Tipos do M10 (Painel e Administração) consumidos pelo SPA.
 *
 * 🔴 Formas alinhadas ao CONTRATO CONGELADO (BINDING). As respostas do backend
 * vêm no envelope `{ data }` do projeto; o painelApi desembrulha e devolve
 * exatamente estes shapes. Nenhum dado pessoal trafega (RN-GLOBAL-004).
 */

import type { FiltroVisitasQuery, PendenciaVisita } from './consulta-types';

/** Marcadores de qualidade (RN-GLOBAL-007). */
export type Marcador = 'OK' | 'CORRIGIDO' | 'ALERTA' | 'ERRO';

/** GET /painel/totais — cards agregados (RF-M10-001). */
export interface Totais {
  readonly arquivos: number;
  readonly visitas: number;
  readonly escolas: number;
  readonly produtivas: number;
  readonly improdutivas: number;
  readonly suspeitos: number;
  readonly editadas: number;
  /** ADR-038: as visitas pela evidência (`visitas = comImagem + semImagem`); ausentes numa API anterior. */
  readonly comImagem?: number;
  readonly semImagem?: number;
}

/** Item do ranking de campos mais corrigidos (RF-M10-003). */
export interface CampoCorrigido {
  readonly campo: string;
  readonly edicoes: number;
}

/** GET /painel/qualidade — percentuais por marcador + ranking (RF-M10-003). */
export interface Qualidade {
  readonly porMarcador: Record<Marcador, number>;
  readonly camposMaisCorrigidos: readonly CampoCorrigido[];
}

/** Ponto do mapa: só visitas com coordenada válida (RN-M10-003, PC-02). */
export interface PontoMapa {
  readonly inep: string | null;
  readonly nome: string | null;
  readonly lat: number;
  readonly lng: number;
  readonly resultado: string | null;
  /** ADR-038: `imagem` se a escola tem alguma visita com imagem, senão `planilha` (ausente numa API anterior). */
  readonly evidencia?: 'imagem' | 'planilha';
}

/** GET /painel/mapa — distribuição por UF + pontos (RF-M10-002). */
export interface Mapa {
  readonly porUf: Record<string, number>;
  readonly pontos: readonly PontoMapa[];
}

/** Status de um reprocessamento. */
export type StatusReprocessamento = 'enfileirado' | 'em_execucao' | 'concluido' | 'falha';

/** Filtro de reprocessamento (subconjunto dos filtros do M7). */
export interface ReprocessamentoFiltro {
  readonly uf?: string;
  readonly municipioIbge?: string;
  readonly marcador?: Marcador;
  readonly tipoDoc?: string;
}

/**
 * Modo do reprocessamento (ADR-035): `revalidar` — "conferir de novo": revalida a leitura
 * guardada de cada visita, na versão em que foi lida, sem nova leitura e sem custo; `reler` —
 * lê de novo na versão pedida (o de sempre).
 */
export type ModoReprocessamento = 'revalidar' | 'reler';

/** De onde veio o disparo (ADR-036); `avancado` = o formulário técnico. */
export type OrigemReprocessamento = 'ficha' | 'lista' | 'painel' | 'avancado';

/**
 * Body do POST /reprocessamentos (contrato). ADR-036: um critério — `filtroVisitas` (o filtro aplicado da lista),
 * `visitaId` (ficha) ou `pendencia` (painel) — com a `origem`; sem versão em `reler` = a vigente. O pedido antigo
 * (formulário técnico) usa `filtro`/`visitaIds` e a versão.
 */
export interface CriarReprocessamentoInput {
  /** Ausente = `reler` (contrato). */
  readonly modo?: ModoReprocessamento;
  /** Formulário técnico: a versão pedida em `reler`. Ausente = a vigente (ADR-036). */
  readonly versaoPrompt?: string;
  readonly filtro?: ReprocessamentoFiltro;
  readonly visitaIds?: readonly string[];
  readonly origem?: Exclude<OrigemReprocessamento, 'avancado'>;
  readonly filtroVisitas?: FiltroVisitasQuery;
  readonly visitaId?: string;
  readonly pendencia?: PendenciaVisita;
}

/** O critério da estimativa: o filtro aplicado da lista (inclusive `pendencia`) ou uma visita. */
export type CriterioEstimativa = { readonly visitaId: string } | { readonly filtro: FiltroVisitasQuery };

/** Resposta de GET /reprocessamentos/estimativa (ADR-036). */
export interface EstimativaReprocessamento {
  readonly total: number;
  readonly versaoVigente: string;
  readonly lerDeNovo: {
    readonly visitas: number;
    readonly jaNaVigente: number;
    /** ADR-038: as do critério sem imagem — ficam de fora do "ler de novo" (ausente numa API anterior). */
    readonly semImagem?: number;
    /** visitas × custo médio de uma leitura; null sem histórico. */
    readonly custoEstimadoUsd: number | null;
  };
  /** Acima desse custo, "Ler a imagem de novo" pede uma segunda confirmação. */
  readonly confirmarAcimaUsd: number;
}

/** Item de GET /reprocessamentos (ADR-036). */
export interface ReprocessamentoResumoItem {
  readonly reprocessamentoId: string;
  readonly modo: ModoReprocessamento;
  readonly origem: OrigemReprocessamento;
  readonly criterio: unknown;
  readonly disparadoPor: string | null;
  readonly criadoEm: string;
  readonly status: StatusReprocessamento;
  readonly afetadas: number;
  readonly processadas: number;
  readonly mudaram: number;
}

/** Visitas que passaram de um marcador para outro (`antes` nulo = a visita não existia). */
export interface TransicaoMarcador {
  readonly antes: Marcador | null;
  readonly depois: Marcador;
  readonly visitas: number;
}

/** Resposta de GET /painel/sugestoes (ADR-036). */
export interface SugestoesReprocessamento {
  readonly promptAntigo: { readonly visitas: number; readonly custoEstimadoUsd: number | null; readonly versaoVigente: string };
  readonly regrasAntigas: { readonly visitas: number };
}

/** Resposta do POST /reprocessamentos. */
export interface CriarReprocessamentoResponse {
  readonly reprocessamentoId: string;
  readonly status: 'enfileirado';
  readonly estimativaAfetadas: number;
}

/** Resposta do GET /reprocessamentos/{id} — ADR-036: o item da lista + o resumo e os lotes (ausentes numa API antiga). */
export interface ReprocessamentoStatus extends Partial<Omit<ReprocessamentoResumoItem, 'reprocessamentoId' | 'status' | 'afetadas' | 'processadas' | 'criadoEm'>> {
  readonly reprocessamentoId: string;
  readonly status: StatusReprocessamento;
  readonly afetadas: number;
  readonly processadas: number;
  readonly criadoEm: string;
  readonly resumo?: {
    readonly processadas: number;
    readonly mudaram: number;
    readonly iguais: number;
    readonly transicoes: readonly TransicaoMarcador[];
  };
  readonly lotes?: ReadonlyArray<{ readonly loteId: string; readonly versaoPrompt: string }>;
}

/** Body do PATCH /config (white-label, todos opcionais). */
/** Arquivo do logo no PATCH /config (C-M1): PNG/JPEG até 512 KB, em base64. */
export interface LogoArquivo {
  readonly contentType: 'image/png' | 'image/jpeg';
  readonly conteudoBase64: string;
}

export interface AtualizarConfigInput {
  readonly nomeSistema?: string;
  readonly corPrimaria?: string;
  readonly logo?: LogoArquivo;
}

/** GET /config e resposta do PATCH /config (white-label). */
export interface ConfigWhiteLabel {
  readonly nomeSistema: string;
  readonly logoUrl: string;
  readonly corPrimaria: string;
}

/**
 * Motivos dos documentos sem visita (ADR-034) — os mesmos de `GET /painel/sem-visita`,
 * na ordem em que o quadro os mostra.
 */
export const MOTIVOS_SEM_VISITA = [
  'outroDocumento',
  'declaracao',
  // ADR-048: a evidência complementar sem visita ligada (ausente numa API anterior).
  'evidenciaSemVisita',
  'formatoNaoAceito',
  'falhaPreprocessamento',
  'paginasAlemDoLimite',
  'falhaExtracao',
  'aguardando',
] as const;
export type MotivoSemVisita = (typeof MOTIVOS_SEM_VISITA)[number];

/** O motivo que só a API com as evidências complementares (ADR-048) manda. */
type MotivoNovoSemVisita = 'evidenciaSemVisita';

/**
 * GET /painel/sem-visita: documentos = visitas + semVisita.total; com as evidências complementares (ADR-048),
 * documentos = visitas + evidenciasLigadas + semVisita.total.
 */
export interface DocumentosSemVisita {
  readonly caminhos: number;
  readonly caminhosDuplicados: number;
  readonly documentos: number;
  readonly visitas: number;
  /** ADR-048: as evidências complementares ligadas a uma visita — fora de "sem visita". Ausente numa API anterior. */
  readonly evidenciasLigadas?: number;
  readonly semVisita: { readonly total: number } & Readonly<Record<Exclude<MotivoSemVisita, MotivoNovoSemVisita>, number>> &
    Readonly<Partial<Record<MotivoNovoSemVisita, number>>>;
  /** ADR-040: as planilhas de instalações das cargas — fonte de visitas, fora da conta dos documentos. */
  readonly planilhas?: {
    readonly importadas: number;
    readonly recusadas: number;
    /** Req 1.4 (ADR-046): a que não é de nenhum tipo conhecido — fora das recusadas. Ausente na API anterior. */
    readonly naoReconhecidas?: number;
    readonly emAnalise: number;
  };
}

/** Item de GET /painel/sem-visita/arquivos (sem dado pessoal). */
export interface ArquivoSemVisita {
  readonly caminhoOriginal: string;
  readonly outrosCaminhos: number;
  readonly pagina: number | null;
  readonly motivo: MotivoSemVisita;
  readonly detalhe: string | null;
}

export interface PaginaArquivosSemVisita {
  readonly itens: readonly ArquivoSemVisita[];
  readonly nextCursor: string | null;
}
